<script>
  import LoanModeller from "./lib/LoanModeller.svelte";
  import TakeHome from "./lib/TakeHome.svelte";
  import Pension from "./lib/Pension.svelte";
  import Gift from "./lib/Gift.svelte";
  import Dashboard from "./lib/Dashboard.svelte";

  const TABS = [
    { id: "dashboard", label: "Dashboard" },
    { id: "loan", label: "Your loan" },
    { id: "takehome", label: "Take home" },
    { id: "pension", label: "Pension" },
    { id: "gift", label: "Gift" },
  ];

  let activeTab = $state("dashboard");

  function handleTabClick(event) {
    const id = event.currentTarget.dataset.tabId;
    if (id) activeTab = id;
  }
</script>

<header>
  <div class="header-inner">
    <h1>
      <span class="brand-mark">StudiFi</span>
      <span class="brand-sub">UK personal-finance optimiser</span>
    </h1>
    <nav>
      {#each TABS as tab (tab.id)}
        <button
          type="button"
          class="tab"
          class:active={activeTab === tab.id}
          data-tab-id={tab.id}
          onclick={handleTabClick}
          onpointerdown={handleTabClick}
        >
          {tab.label}
        </button>
      {/each}
    </nav>
  </div>
</header>

<main>
  {#if activeTab === "dashboard"}<Dashboard />{/if}
  {#if activeTab === "loan"}<LoanModeller />{/if}
  {#if activeTab === "pension"}<Pension />{/if}
  {#if activeTab === "gift"}<Gift />{/if}
  {#if activeTab === "takehome"}<TakeHome />{/if}
</main>

<footer>
  <p>
    Information &amp; calculators only, not financial advice.
    Consider taking advice from an FCA-regulated adviser before acting.
  </p>
  <p class="footer-sub">
    Data current to April 2025 (2025–26 tax year).
    Calc engine open-source — see <code>calc/</code>.
    <span class="build-tag">build 2026-09-25h</span>
  </p>
</footer>

<style>
  header {
    background: var(--card);
    border-bottom: 1px solid var(--card-border);
    padding: 1rem;
    padding-top: max(1rem, env(safe-area-inset-top));
    padding-left: max(1rem, env(safe-area-inset-left));
    padding-right: max(1rem, env(safe-area-inset-right));
    box-shadow: 0 1px 0 rgba(15, 61, 62, 0.03);
  }
  .header-inner {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  h1 { display: flex; align-items: baseline; gap: 0.65rem; flex-wrap: wrap; }
  .brand-mark {
    font-size: 1.35rem;
    font-weight: 800;
    color: var(--accent);
    letter-spacing: -0.02em;
  }
  .brand-sub {
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--fg-muted);
  }
  nav {
    display: flex;
    gap: 0.25rem;
    flex-wrap: wrap;
  }
  .tab {
    background: transparent;
    border: 1px solid transparent;
    color: var(--fg-muted);
    padding: 0.45rem 0.85rem;
    border-radius: 999px;
    font-size: 0.9rem;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    -webkit-user-select: none;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
  }
  /* Only apply :hover styles on devices that ACTUALLY hover (mice/trackpads).
     iOS Safari otherwise fires :hover on first tap and eats the click,
     causing off-by-one tab switching. */
  @media (hover: hover) {
    .tab:hover {
      color: var(--fg);
      background: var(--accent-tint);
    }
  }
  .tab.active {
    background: var(--accent);
    color: var(--accent-fg);
    border-color: var(--accent);
  }

  main {
    max-width: 1200px;
    margin: 0 auto;
    padding: 1.5rem 1rem;
    padding-left: max(1rem, env(safe-area-inset-left));
    padding-right: max(1rem, env(safe-area-inset-right));
  }

  footer {
    max-width: 1200px;
    margin: 3rem auto 0;
    padding: 1.5rem 1rem 3rem;
    padding-bottom: max(1.5rem, env(safe-area-inset-bottom));
    color: var(--fg-muted);
    font-size: 0.82rem;
    text-align: center;
    border-top: 1px solid var(--card-border);
  }
  footer p { margin: 0.25rem 0; }
  .footer-sub {
    color: var(--fg-subtle);
    font-size: 0.75rem;
  }
  code {
    background: var(--accent-tint);
    color: var(--accent);
    padding: 0.05em 0.35em;
    border-radius: 0.3em;
    font-size: 0.9em;
  }
  .build-tag {
    display: inline-block;
    margin-left: 0.5rem;
    padding: 0.05em 0.4em;
    background: var(--bg-2);
    border-radius: 0.3em;
    font-family: monospace;
    font-size: 0.85em;
    color: var(--fg-muted);
  }

  @media (min-width: 720px) {
    header { padding: 1.25rem 1.75rem; }
    .header-inner {
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      gap: 1.5rem;
    }
    main { padding: 2rem 1.75rem; }
  }
</style>
